import { clampColumnCount, MAX_CONTENT_COLUMNS, selectionColumnWidthPercent } from "@/lib/cms-puck/blocks/column-count";
import { cmsNew } from "@/lib/cms/cms-new-i18n";

/**
 * Module NEUTRE « selection » — KEEP IN SYNC avec
 * inventozen-frontpublic/src/cms-puck/selection.ts.
 *
 * Types + fonctions PURES du bloc « Sélection d'œuvres » (ArtworkSelection) :
 * items (Œuvre OU Multiple), mise en page (colonnes/alignement), et bascules
 * d'infos affichées. AUCUN import React/Puck/UI. Rétro-compat : un bloc legacy
 * (prop `artworkIds` CSV + `layout` grid-2/grid-3/carousel) est lu via
 * `resolveSelectionItems` / `resolveLayout`.
 */

/**
 * Type discriminant d'un item de catalogue. `artwork`/`multipleprint` couvrent le
 * bloc historique `ArtworkSelection` ; `image` = média LIBRE de la médiathèque
 * (id = Attachment.id, image/vidéo/embed — parité ancien CMS `Selection.imageId`,
 * Sélection uniquement, PAS ItemSpotlight) ; les autres sont les tables
 * additionnelles référençables par le bloc mono-sélection `ItemSpotlight`.
 * KEEP IN SYNC avec `CatalogItemKind` côté API (cms-puck.service.ts).
 */
export type SelectionItemType =
  | "artwork"
  | "multipleprint"
  | "image"
  | "writing"
  | "photography"
  | "document"
  | "audiovisual"
  | "tirage"
  | "multiplesample"
  | "sculpture"
  | "exhibition"
  | "bibliography";

/**
 * Un item sélectionné : une entité de catalogue (ou un média libre), par
 * type + id. `caption` = légende custom TRADUISIBLE par item (parité ancien CMS
 * `Selection.caption`, affichée par la bascule `customField`) — extraite vers
 * ContentString (clé `<blockId>.captions.<type>:<id>`, cf. api.ts BO) : le
 * layout stocké reste NEUTRE (caption absente).
 */
export type SelectionItem = {
  type: SelectionItemType;
  id: string;
  caption?: string;
};

const CATALOG_ITEM_TYPE_DEFS: {
  type: SelectionItemType;
  labelKey: `cms.new.${string}`;
  labelFr: string;
}[] = [
  { type: "artwork", labelKey: "cms.new.options.catalogType.artwork", labelFr: "Œuvres" },
  { type: "multipleprint", labelKey: "cms.new.options.catalogType.multipleprint", labelFr: "Multiples & estampes" },
  {
    type: "photography",
    // Clé dédiée : l'ancienne `…photography` reste « Photographies » en BDD.
    labelKey: "cms.new.options.catalogType.photographyDocumentary",
    labelFr: "Documentaires photographiques",
  },
  { type: "writing", labelKey: "cms.new.options.catalogType.writing", labelFr: "Écrits" },
  { type: "document", labelKey: "cms.new.options.catalogType.document", labelFr: "Documents" },
  { type: "audiovisual", labelKey: "cms.new.options.catalogType.audiovisual", labelFr: "Audiovisuel" },
  {
    // Clé dédiée : l'ancienne `…tirage` reste « Tirages » en BDD.
    type: "tirage",
    labelKey: "cms.new.options.catalogType.tiragePhoto",
    labelFr: "Tirages photo",
  },
  {
    type: "multiplesample",
    labelKey: "cms.new.options.catalogType.multiplesample",
    labelFr: "Exemplaires Multiples",
  },
  {
    type: "sculpture",
    labelKey: "cms.new.options.catalogType.sculpture",
    labelFr: "Exemplaires Sculptures",
  },
  { type: "exhibition", labelKey: "cms.new.options.catalogType.exhibition", labelFr: "Expositions" },
  { type: "bibliography", labelKey: "cms.new.options.catalogType.bibliography", labelFr: "Bibliographies" },
];

export function getCatalogItemTypes(): { type: SelectionItemType; label: string }[] {
  return CATALOG_ITEM_TYPE_DEFS.map((d) => ({
    type: d.type,
    label: cmsNew(d.labelKey, d.labelFr),
  }));
}


/** Clé d'un item dans la map `refs.catalogItems` côté front : `${type}:${id}`. */
export function catalogItemKey(item: {
  type: SelectionItemType;
  id: string;
}): string {
  return `${item.type}:${item.id}`;
}

export type SelectionColumns = number;
/** Affichage : grille (défaut) ou carrousel Embla (1 item par slide). */
export type SelectionDisplayMode = "grid" | "carousel";
/**
 * Mode d'alignement (façon ancien CMS front public) :
 *  - `center` : lignes de N, images centrées verticalement dans la ligne ;
 *  - `top`    : lignes de N, images alignées en HAUT ;
 *  - `grid`   : flux en colonnes (flex-wrap), images en taille naturelle.
 * Dans TOUS les modes, les images gardent leur ratio (jamais de carré/recadrage).
 */
export type SelectionAlignment = "center" | "top" | "grid";
export type SelectionCaptionAlign = "left" | "center" | "right";

/** Largeur d'un item selon le nombre de colonnes (mirroir ancien CMS). */
export const SELECTION_COLUMN_WIDTH_PERCENT: Record<2 | 3 | 4, number> = {
  2: 45,
  3: 30,
  4: 22,
};

/** @deprecated Préférer `selectionColumnWidthPercent` depuis `column-count.ts`. */
export function resolveSelectionColumnWidthPercent(columns: number): number {
  return selectionColumnWidthPercent(columns);
}

/**
 * Bascules « quelles infos afficher sous chaque œuvre » (jeu ancien CMS).
 * `customField` = 10ᵉ bascule (parité `visibleCustomField`) : affiche la légende
 * custom par item (`SelectionItem.caption`) sous les infos.
 */
export type SelectionVisibility = {
  titles: boolean;
  authors: boolean;
  dates: boolean;
  techniqueAndSupport: boolean;
  dimensions: boolean;
  signature: boolean;
  backText: boolean;
  currentCollection: boolean;
  registerNumber: boolean;
  customField: boolean;
};

/** Défaut = rendu actuel (titre + auteurs + date), le reste masqué. */
export const DEFAULT_SELECTION_VISIBILITY: SelectionVisibility = {
  titles: true,
  authors: true,
  dates: true,
  techniqueAndSupport: false,
  dimensions: false,
  signature: false,
  backText: false,
  currentCollection: false,
  registerNumber: false,
  customField: false,
};

const DISPLAY_MODES: readonly SelectionDisplayMode[] = ["grid", "carousel"];
const ALIGNMENTS: readonly SelectionAlignment[] = ["center", "top", "grid"];
const CAPTION_ALIGNS: readonly SelectionCaptionAlign[] = [
  "left",
  "center",
  "right",
];

export function normalizeSelectionVisibility(value: unknown): SelectionVisibility {
  const v = (value ?? {}) as Partial<SelectionVisibility>;
  const bool = (x: unknown, dflt: boolean) =>
    typeof x === "boolean" ? x : dflt;
  return {
    titles: bool(v.titles, DEFAULT_SELECTION_VISIBILITY.titles),
    authors: bool(v.authors, DEFAULT_SELECTION_VISIBILITY.authors),
    dates: bool(v.dates, DEFAULT_SELECTION_VISIBILITY.dates),
    techniqueAndSupport: bool(
      v.techniqueAndSupport,
      DEFAULT_SELECTION_VISIBILITY.techniqueAndSupport,
    ),
    dimensions: bool(v.dimensions, DEFAULT_SELECTION_VISIBILITY.dimensions),
    signature: bool(v.signature, DEFAULT_SELECTION_VISIBILITY.signature),
    backText: bool(v.backText, DEFAULT_SELECTION_VISIBILITY.backText),
    currentCollection: bool(
      v.currentCollection,
      DEFAULT_SELECTION_VISIBILITY.currentCollection,
    ),
    registerNumber: bool(
      v.registerNumber,
      DEFAULT_SELECTION_VISIBILITY.registerNumber,
    ),
    customField: bool(v.customField, DEFAULT_SELECTION_VISIBILITY.customField),
  };
}

/**
 * Résout la liste ordonnée d'items. Si `items[]` est présent/valide → l'utiliser ;
 * sinon rétro-compat : parse l'ancien `artworkIds` CSV → items de type `artwork`.
 */
export function resolveSelectionItems(raw: {
  items?: unknown;
  artworkIds?: unknown;
}): SelectionItem[] {
  if (Array.isArray(raw.items)) {
    const out: SelectionItem[] = [];
    for (const it of raw.items) {
      const o = it as { type?: unknown; id?: unknown; caption?: unknown };
      const id = typeof o?.id === "string" ? o.id.trim() : "";
      if (!id) continue;
      const type: SelectionItemType =
        o.type === "multipleprint"
          ? "multipleprint"
          : o.type === "image"
            ? "image"
            : "artwork";
      const caption =
        typeof o.caption === "string" && o.caption ? o.caption : undefined;
      out.push(caption ? { type, id, caption } : { type, id });
    }
    return out;
  }
  // Legacy : CSV d'ids d'œuvres.
  return String(raw.artworkIds ?? "")
    .split(",")
    .map((s) => s.trim())
    .filter(Boolean)
    .map((id) => ({ type: "artwork" as const, id }));
}

/** Ids par type (pour les requêtes / le picker). */
export function selectionIdsByType(items: SelectionItem[]): {
  artworkIds: string[];
  multipleprintIds: string[];
  imageIds: string[];
} {
  const artworkIds: string[] = [];
  const multipleprintIds: string[] = [];
  const imageIds: string[] = [];
  for (const it of items) {
    if (it.type === "multipleprint") multipleprintIds.push(it.id);
    else if (it.type === "image") imageIds.push(it.id);
    else artworkIds.push(it.id);
  }
  return { artworkIds, multipleprintIds, imageIds };
}

/**
 * Résout `{ columns, alignment }` en tolérant les valeurs LEGACY :
 *  - `layout` `grid-2`/`grid-3`/`carousel` → colonnes 2/3 + alignement `grid` ;
 *  - `align` `start`→`top`, `space-around`→`grid`, `center`→`center`.
 */
export function resolveSelectionLayout(raw: {
  layout?: unknown;
  columns?: unknown;
  alignment?: unknown;
  align?: unknown;
}): { columns: SelectionColumns; alignment: SelectionAlignment } {
  let columns = clampColumnCount(raw.columns, {
    min: 1,
    max: MAX_CONTENT_COLUMNS,
    // 4 = défaut fréquent des grilles de sélection / rubriques (ancien CMS).
    fallback: 4,
  });
  if (raw.columns == null && raw.layout === "grid-2") {
    columns = 2;
  }

  // Alignement : nouvelle prop, sinon mappe l'ancien `align`, sinon `grid`.
  let alignment: SelectionAlignment;
  if (ALIGNMENTS.includes(raw.alignment as SelectionAlignment)) {
    alignment = raw.alignment as SelectionAlignment;
  } else if (raw.align === "start") {
    alignment = "top";
  } else if (raw.align === "space-around" || raw.align === "center") {
    alignment = raw.align === "center" ? "center" : "grid";
  } else {
    alignment = "grid";
  }
  return { columns, alignment };
}

export function normalizeAlignment(value: unknown): SelectionAlignment {
  return ALIGNMENTS.includes(value as SelectionAlignment)
    ? (value as SelectionAlignment)
    : "grid";
}

export function normalizeCaptionAlign(value: unknown): SelectionCaptionAlign {
  return CAPTION_ALIGNS.includes(value as SelectionCaptionAlign)
    ? (value as SelectionCaptionAlign)
    : "left";
}

/**
 * Mode d'affichage : nouvelle prop `displayMode`, sinon legacy `layout:"carousel"`.
 */
export function normalizeSelectionDisplayMode(value: unknown): SelectionDisplayMode {
  return DISPLAY_MODES.includes(value as SelectionDisplayMode)
    ? (value as SelectionDisplayMode)
    : "grid";
}

export function resolveSelectionDisplayMode(raw: {
  displayMode?: unknown;
  layout?: unknown;
}): SelectionDisplayMode {
  if (DISPLAY_MODES.includes(raw.displayMode as SelectionDisplayMode)) {
    return raw.displayMode as SelectionDisplayMode;
  }
  if (raw.layout === "carousel") return "carousel";
  return "grid";
}

/* -------------------------------------------------------------------------- */
/* SelectionSections — catégories natives (parité ancien CMS)                 */
/* -------------------------------------------------------------------------- */

/** Mode de la sélection : grille de rubriques, ou œuvres à plat. */
export type SelectionSectionsMode = "categorized" | "global";

/**
 * Une rubrique / catégorie de sélection (parité `CmsSection` + ses `Selection`).
 * `title` / `description` = ContentString HTML
 * (`<blockId>.categories.<id>.title|description`).
 * Légendes d'items : `<blockId>.captions.<catId>.<type>:<id>`.
 */
export type SelectionCategory = {
  id: string;
  thumbnailAttachmentId?: string | null;
  /** Titre riche (HTML TipTap), comme `description`. */
  title: string;
  description: string;
  items: SelectionItem[];
  columns: SelectionColumns;
  alignment: SelectionAlignment;
  captionAlign: SelectionCaptionAlign;
  visibility: SelectionVisibility;
};

export function createEmptySelectionCategory(id: string): SelectionCategory {
  return {
    id,
    thumbnailAttachmentId: null,
    title: "",
    description: "",
    items: [],
    // Défaut 4 = parité fréquent ancien CMS (grille d'œuvres de rubrique).
    columns: 4,
    alignment: "center",
    captionAlign: "left",
    visibility: { ...DEFAULT_SELECTION_VISIBILITY },
  };
}

/** Texte brut du titre rubrique (HTML TipTap → plain, pour labels / slug). */
export function selectionCategoryPlainTitle(
  title: string | null | undefined,
): string {
  if (!title) return "";
  return title
    .replace(/<[^>]*>/g, " ")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim();
}

export function normalizeSelectionSectionsMode(
  value: unknown,
): SelectionSectionsMode {
  return value === "global" ? "global" : "categorized";
}

/** Normalise un tableau de catégories (ids manquants ignorés). */
export function normalizeSelectionCategories(
  value: unknown,
): SelectionCategory[] {
  if (!Array.isArray(value)) return [];
  const out: SelectionCategory[] = [];
  for (const raw of value) {
    if (!raw || typeof raw !== "object") continue;
    const o = raw as Partial<SelectionCategory>;
    const id = typeof o.id === "string" ? o.id.trim() : "";
    if (!id) continue;
    const { columns, alignment } = resolveSelectionLayout({
      columns: o.columns,
      alignment: o.alignment,
    });
    out.push({
      id,
      thumbnailAttachmentId:
        typeof o.thumbnailAttachmentId === "string"
          ? o.thumbnailAttachmentId
          : null,
      title: typeof o.title === "string" ? o.title : "",
      description: typeof o.description === "string" ? o.description : "",
      items: resolveSelectionItems({ items: o.items }),
      columns,
      alignment,
      captionAlign: normalizeCaptionAlign(o.captionAlign),
      visibility: normalizeSelectionVisibility(o.visibility),
    });
  }
  return out;
}
